/**
 * Settings → Notifications → the Push section — V6-01 §4.
 *
 * One primary button raises the browser's permission prompt, and nothing else
 * in the app ever does. The device list is what makes the Push column in the
 * matrix honest: ticking a box changes nothing if no browser is registered,
 * and without this list there is no way to see that.
 */

import { BellRing, Loader2, MonitorSmartphone, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ApiError } from '@/lib/api';
import {
  PushSetupError,
  pushSupported,
  requestPushPermission,
  useEnablePush,
  usePushDevices,
  usePushPublicKey,
  useRemovePushDevice,
} from './pushApi';

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function PushDevices() {
  const { data: devices = [], isLoading } = usePushDevices();
  const { data: key } = usePushPublicKey();
  const enable = useEnablePush();
  const remove = useRemovePushDevice();

  const supported = pushSupported();

  /**
   * The gesture, and the order that matters — V7-01 §1.
   *
   * `requestPushPermission()` is the first statement, with nothing awaited
   * before it. V6-01 fetched the VAPID key first, which spent the click's
   * transient activation and left Firefox resolving `default` without ever
   * showing a prompt — the site never even reached the permission list.
   * `pushGesture.test.ts` enforces this ordering structurally.
   */
  async function onEnable(): Promise<void> {
    const permission = await requestPushPermission();

    if (permission !== 'granted') {
      // Said out loud, and distinguishing "you closed it" from "it is
      // blocked" — they need opposite next steps, and the diagnostics panel
      // below spells the second one out per browser.
      toast.error(
        permission === 'denied'
          ? 'Notifications are blocked for this site — see the diagnostics below for how to unblock them.'
          : 'The browser prompt was closed without a choice. Press the button again when you are ready.',
      );
      return;
    }

    try {
      const device = await enable.mutateAsync();
      toast.success(`Push enabled on ${device.label}`);
    } catch (err) {
      const message =
        err instanceof PushSetupError || err instanceof ApiError
          ? err.message
          : 'Could not enable push on this device.';
      toast.error(message);
    }
  }

  return (
    <section className="vt-surface flex flex-col gap-2 p-3" data-testid="push-devices">
      <div className="flex items-center gap-2">
        <BellRing className="size-4 text-[var(--text-muted)]" />
        <span className="flex-1 text-[13px] font-medium">Push notifications</span>
        {key && !key.configured && <Badge tone="warn">not configured</Badge>}
      </div>

      <p className="text-[12px] vt-muted">
        A push arrives only when the app is closed — with it open, the in-app notification already
        has it. Each browser you use is its own device here.
      </p>

      {!supported && (
        <p className="text-[12px] text-[var(--warn)]" role="status">
          This browser cannot receive push notifications. On iPhone and iPad, add VirtueTasks to the
          Home Screen first (iOS 16.4 or later); private windows can never subscribe.
        </p>
      )}

      <div className="flex flex-col gap-1" data-testid="push-device-list">
        {isLoading ? null : devices.length === 0 ? (
          <p className="text-[12px] vt-muted">No devices yet.</p>
        ) : (
          devices.map((device) => (
            <div
              key={device._id}
              data-testid="push-device-row"
              data-label={device.label}
              className="flex items-center gap-2 rounded-[6px] border border-[var(--border)] px-2 py-1.5 text-[13px]"
            >
              <MonitorSmartphone className="size-3.5 text-[var(--text-muted)]" />
              <span className="min-w-0 flex-1 truncate">{device.label}</span>
              <span className="text-[11px] vt-muted">added {when(device.createdAt)}</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${device.label}`}
                data-testid="push-device-remove"
                disabled={remove.isPending}
                onClick={() => remove.mutate(device._id)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))
        )}
      </div>

      <Button
        variant="primary"
        size="sm"
        className="self-start"
        data-testid="enable-push"
        disabled={!supported || enable.isPending}
        onClick={() => void onEnable()}
      >
        {enable.isPending ? <Loader2 className="animate-spin" /> : <BellRing />}
        Enable on this device
      </Button>
    </section>
  );
}
